// @flow
import invariant from 'invariant';
import * as React from 'react';
import styled from 'styled-components';
import * as localStorage from 'utils/localStorage';
import googleMaps from 'utils/googleMaps';
import { ClearButton } from 'components/Button';
import LocationIcon from 'location.svg';
import { TooltipStandalone } from 'components/TooltipStandalone';
import Input from './Input';

const testMode = localStorage.getItem('testMode');

const getCity = (addressComponents: AddressComponents) =>
  addressComponents.locality ||
  addressComponents.postal_town ||
  addressComponents.sublocality_level_1 ||
  addressComponents.administrative_area_level_3 ||
  addressComponents.administrative_area_level_2;

const makeKeydownEvent = (key: string, charCode: number) => {
  try {
    return new KeyboardEvent('keydown', {
      key,
      keyCode: charCode,
      which: charCode
    });
  } catch (e) {
    // For IE 11
    const keydownEvt = document.createEvent('Events');
    keydownEvt.initEvent('keydown', true, true);
    // $FlowFixMe: flow cannot handle this IE11 magic
    keydownEvt.which = charCode;
    // $FlowFixMe: flow cannot handle this IE11 magic
    keydownEvt.keyCode = charCode;
    return keydownEvt;
  }
};

const selectFirstResultOnEnter = (
  maps: Object,
  autocompleteInput: Object,
  onEnter: () => any
) => {
  const addEventListener = autocompleteInput.addEventListener
    ? autocompleteInput.addEventListener
    : autocompleteInput.attachEvent;

  const addEventListenerWrapper = (
    type: string,
    listener: EventListener,
    optionsOrUseCapture?: EventListenerOptionsOrUseCapture
  ) => {
    // Simulate a 'down arrow' keypress on hitting 'return' when no suggestion
    // is selected and then trigger the original listener.
    // If the dropdown is NOT active, run any onEnter code
    if (type === 'keydown') {
      const originalListener = listener;
      listener = (event: Event) => {
        invariant(
          event instanceof KeyboardEvent,
          `Invalid event type: ${event.constructor.name}`
        );
        if (
          (event.which === 9 || event.which === 13) &&
          !document.querySelector('.pac-item-selected')
        ) {
          // Run keydown code if autocomplete selection open. Otherwise, onEnter.
          if (document.querySelector('.pac-item')) {
            const keydownEvt = makeKeydownEvent('ArrowDown', 40);
            if (typeof originalListener === 'function') {
              originalListener.apply(event.target, [keydownEvt]);
            } else {
              originalListener.handleEvent.apply(event.target, [keydownEvt]);
            }
          } else {
            onEnter();
          }
        }
        if (typeof originalListener === 'function') {
          originalListener.apply(autocompleteInput, [event]);
        } else {
          originalListener.handleEvent.apply(autocompleteInput, [event]);
        }
      };
    }
    const args = [type, listener];
    if (optionsOrUseCapture) args.push(optionsOrUseCapture);
    addEventListener.apply(autocompleteInput, args);
  };

  autocompleteInput.addEventListener = addEventListenerWrapper;
  autocompleteInput.attachEvent = addEventListenerWrapper;
};

const StyledLocationIcon = styled(LocationIcon)`
  position: absolute;
  top: 0;
  bottom: 0;
  margin-top: auto;
  margin-bottom: auto;
  left: 16px;
`;

const InputContainer = styled.div`
  display: inline-flex;
  position: relative;
  align-items: center;
  width: 100%;
`;

const StyledInput = React.forwardRef<any, InputContainer>(
  (
    {
      showLocationIcon,
      onClear,
      className,
      gMapsErrored,
      setGMapsError,
      ...inputProps
    }: {
      showLocationIcon: boolean,
      onClear: ?() => void,
      className?: string,
      gMapsErrored: boolean,
      setGMapsError: Function,
      'data-qa-id': string
    },
    ref
  ) => (
    <InputContainer className={className}>
      {showLocationIcon && <StyledLocationIcon width="15" height="21" />}
      <Input
        {...inputProps}
        ref={ref}
        height="100%"
        width="100%"
        pl={showLocationIcon ? '38px' : '18px'}
        pr={onClear ? '38px' : '18px'}
      />
      {onClear && (
        <ClearButton
          onClick={onClear}
          data-qa-id={`${inputProps['data-qa-id']}-clear`}
        />
      )}
      {gMapsErrored && (
        <TooltipStandalone>
          {getText(
            'Our geolocation provider (Google Maps) did not load correctly. Please reload the page.'
          )}
        </TooltipStandalone>
      )}
    </InputContainer>
  )
);

type Props = {
  onLocationChange: SearchLocation => void,
  onLocationInputValueChange?: InputEvent => void,
  onEnter?: () => void,
  clearLocation: () => void,
  locationInputValue: string,
  'data-qa-id': string,
  placeholder: ?string,
  showLocationIcon: boolean,
  showClearButton: boolean,
  className?: string,
  setGMapsError: Function,
  includeCity?: boolean,
  includeFull?: boolean,
  onBlur?: Function,
  disabled?: boolean
};

// eslint-disable-next-line react/no-multi-comp
export default class LocationInput extends React.PureComponent<Props> {
  static defaultProps = {
    // eslint-disable-next-line react/default-props-match-prop-types
    'data-qa-id': 'location-input',
    // eslint-disable-next-line react/default-props-match-prop-types
    showLocationIcon: false
  };

  autocompleteInputRef = React.createRef<Element>();

  componentDidMount() {
    this.setupAddressAutocomplete();
  }

  setupAddressAutocomplete = () => {
    const autocompleteInput = this.autocompleteInputRef.current;
    invariant(autocompleteInput, 'Missing autocompleteInput');

    googleMaps()
      .then(maps => {
        const autocomplete = new maps.places.Autocomplete(autocompleteInput, {
          types: ['geocode']
        });

        autocomplete.addListener('place_changed', () => {
          if (autocomplete) {
            const place = autocomplete.getPlace();
            if (place && place.address_components) this.placeChanged(place);
          }
        });

        selectFirstResultOnEnter(
          maps,
          autocompleteInput,
          this.props.onEnter || (() => null)
        );
      })
      .catch(error => {
        this.props.setGMapsError(error.message);
      });
  };

  onKeyPress = (event: SyntheticKeyboardEvent<>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  placeChanged(place: any) {
    const addressComponents = place.address_components.reduce(
      (hash, component) => {
        const attributeName = component.types[0];
        hash[attributeName] = [
          'country',
          'administrative_area_level_1'
        ].includes(attributeName)
          ? component.short_name
          : component.long_name;
        return hash;
      },
      {}
    );

    if (!('country' in addressComponents)) {
      // below: special handling of 'south east asia', 'gaza', etc.
      // i.e. results for which the autocomplete api does not return a country code
      // we will use the iso 3166 user-defined 'XA' code for this situation
      // https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#User-assigned_code_elements

      addressComponents.country = 'XA';
      place.types[0] = 'undefined_country_geocodes';
    }

    const countryCode = addressComponents.country;
    const stateCode = addressComponents.administrative_area_level_1;

    let city;
    let full;

    if (this.props.includeCity) {
      city = getCity(addressComponents);
    }

    if (this.props.includeFull) {
      full = place.formatted_address;
    }

    const boundingBox = place.geometry.viewport.toJSON();
    const boundingBoxN = boundingBox.north;
    const boundingBoxW = boundingBox.west;
    const boundingBoxS = boundingBox.south;
    const boundingBoxE = boundingBox.east;

    this.props.onLocationChange({
      text: place.formatted_address,
      stateCode,
      countryCode,
      boundingBoxN,
      boundingBoxW,
      boundingBoxS,
      boundingBoxE,
      geoType: place.types[0],
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
      full,
      city
    });
  }

  render() {
    const {
      onLocationChange,
      onLocationInputValueChange,
      onEnter,
      showClearButton,
      clearLocation,
      locationInputValue,
      showLocationIcon,
      includeCity,
      includeFull,
      onBlur,
      ...inputProps
    } = this.props;

    return (
      <StyledInput
        showLocationIcon={showLocationIcon}
        aria-label={getText('Location')}
        ref={this.autocompleteInputRef}
        value={locationInputValue || ''}
        onChange={onLocationInputValueChange}
        onBlur={onBlur ? () => onBlur(locationInputValue || '') : undefined}
        onClear={showClearButton || testMode ? clearLocation : null}
        onKeyPress={this.onKeyPress}
        className={this.props.className}
        {...inputProps}
      />
    );
  }
}
